import { useEffect, useState } from "react";
import { createMarketViewWorker } from "../services/createMarketViewWorker";

const cache = new WeakMap();
const emptyNodes = [];

export default function useMarketViewLayout({ enabled, filtered, width, height, packWidth, squareMode, colorScale }) {
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const matches = entry => entry?.filtered === filtered && entry.width === width && entry.height === height &&
    entry.packWidth === packWidth && entry.squareMode === squareMode && entry.colorScale === colorScale;
  const cached = enabled ? cache.get(filtered)?.find(matches) : null;
  const current = cached || (matches(result) ? result : null);
  useEffect(() => {
    if (!enabled || cached || width <= 0 || height <= 60) return;
    let worker;
    let cancelled = false;
    const inputs = { filtered, width, height, packWidth, squareMode, colorScale };
    const fail = error => {
      worker?.terminate();
      if (!cancelled) {
        console.error("Market Mind layout failed:", error);
        setResult({ ...inputs, nodes: emptyNodes, error: error.message || "Unable to prepare Market Mind." });
      }
    };
    setResult(null);
    try {
      worker = createMarketViewWorker();
      worker.onmessage = ({ data }) => {
        if (cancelled) return;
        if (data.error) { fail(new Error(data.error)); return; }
        worker.terminate();
        const next = { ...inputs, nodes: data.nodes, error: "" };
        const entries = cache.get(filtered) || [];
        entries.push(next);
        if (entries.length > 4) entries.shift();
        cache.set(filtered, entries);
        setResult(next);
      };
      worker.onerror = event => fail(new Error(event.message || "Market Mind worker failed."));
      worker.onmessageerror = () => fail(new Error("Market Mind layout could not be received."));
      worker.postMessage({
        rows: filtered.map(game => ({
          ...game, value: game.mindshare, ms: game.mindshare, growth: game.growthPT || 0,
          color: colorScale(game.mindshare), image: game.imageUrl || game.image,
        })),
        width, height, packWidth, squareMode,
      });
    } catch (error) { fail(error); }
    return () => { cancelled = true; worker?.terminate(); };
  }, [enabled, filtered, width, height, packWidth, squareMode, colorScale, cached, attempt]);
  return {
    nodes: current?.nodes || emptyNodes,
    pending: enabled && !current,
    error: current?.error || "",
    retry: () => setAttempt(value => value + 1),
  };
}
